import React, { useEffect, useRef } from 'react';
import { SupervisaoFinalizadaLinha } from '../../../interfaces/PortalCotacoes';
import { CotacaoBadge } from './PortalBadges';
// Overlay compartilhado dos modais do sistema (definido no AddCarModal e usado por 16
// modais). Importado explicitamente para não depender de outro modal ter entrado no
// bundle antes deste.
import '../../../components/modal/add-car/AddCarModal.css';
// `pc-btn`/`pc-btn--*` vivem aqui. O import é explícito porque o modal é renderizado fora
// da árvore da página no DOM apenas por convenção — depender do CSS que a tela importa é
// o acoplamento invisível que gerou o BUG-V74.
import '../pages/PortalCotacoes.css';
import './FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Detalhes de uma cotação FINALIZADA — abre pelo botão "Detalhes" da aba Finalizadas da
 * Supervisão (14/08/2026, pedido do Victor: "mostre somente Cotação, Veículo, Desfecho,
 * Histórico e Ações; o resto num modal").
 *
 * A tabela ficou com o que se lê de relance; aqui vive o que se lê quando a pergunta já é
 * sobre **aquela** cotação: comprador, datas, fornecedores convidados, o que o gestor
 * decidiu e quem participou.
 *
 * ⚠️ **Não repete o histórico.** O passo a passo é a Auditoria de Cotações — o botão do
 * rodapé leva para lá, com a cotação já escolhida. Duas versões da mesma história
 * divergiriam na primeira mudança de regra (mesma decisão das pontes veículo↔cotação).
 *
 * As classes vêm de `FornecedorDetalhesModal.css`: é a receita visual dos modais do
 * Portal (cabeçalho cinza, seções com `h3` laranja, grade de rótulo/valor em cartões).
 * Reusar a folha em vez de copiá-la é a regra do projeto — o prefixo `fornecedor-` no
 * nome das classes é herança de onde ela nasceu, e renomear a família inteira é item de
 * backlog, não trabalho desta entrega.
 */

interface CotacaoFinalizadaModalProps {
  cotacao: SupervisaoFinalizadaLinha;
  /** Frase pronta do desfecho do gestor — a mesma da tabela, calculada na tela. */
  resumoGestor: string;
  onFechar: () => void;
  onVerHistorico: () => void;
  onAbrirCotacao: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

function formatarDataHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Quanto tempo a cotação levou do começo ao fim, em dias corridos.
 *
 * `null` quando falta um dos marcos — e a tela mostra "—" em vez de zero: registro antigo
 * sem a data de encerramento não pode virar "levou menos de um dia".
 */
function duracaoEmDias(criadaEm: string, finalizadaEm: string | null): number | null {
  if (!finalizadaEm) return null;
  const ms = new Date(finalizadaEm).getTime() - new Date(criadaEm).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

const CotacaoFinalizadaModal: React.FC<CotacaoFinalizadaModalProps> = ({
  cotacao,
  resumoGestor,
  onFechar,
  onVerHistorico,
  onAbrirCotacao,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onFechar();
    };

    const teclaEsc = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', teclaEsc);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', teclaEsc);
    };
  }, [onFechar, sidebarRef]);

  const dias = duracaoEmDias(cotacao.criadaEm, cotacao.finalizadaEm);

  return (
    <div className="add-car-modal-overlay">
      <div className="fornecedor-modal-content" ref={modalRef} role="dialog" aria-label={`Detalhes da cotação ${cotacao.numero}`}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>{cotacao.numero}</h2>
            <span className="fornecedor-modal-subtitulo">
              {cotacao.veiculo ?? 'Veículo não informado'}
              {cotacao.placa ? ` · ${cotacao.placa}` : ''}
            </span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          <div className="fornecedor-modal-secao">
            <h3>Desfecho</h3>
            <div className="fornecedor-info-grid">
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Situação</span>
                <span className="fornecedor-info-value">
                  <CotacaoBadge status={cotacao.status} />
                </span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Criada em</span>
                <span className="fornecedor-info-value">{formatarDataHora(cotacao.criadaEm)}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Finalizada em</span>
                <span className="fornecedor-info-value">{formatarDataHora(cotacao.finalizadaEm)}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Duração</span>
                <span className="fornecedor-info-value">
                  {dias === null ? '—' : `${dias} ${dias === 1 ? 'dia' : 'dias'}`}
                </span>
              </div>
            </div>
            {cotacao.status === 'CANCELADA' && (
              <p className="fornecedor-modal-hint" style={{ marginTop: 16, marginBottom: 0 }}>
                <strong>Motivo do cancelamento:</strong>{' '}
                {cotacao.cancelamentoMotivo || 'não foi registrado à época.'}
              </p>
            )}
          </div>

          <div className="fornecedor-modal-secao">
            <h3>Quem conduziu</h3>
            <div className="fornecedor-info-grid">
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Comprador</span>
                <span className="fornecedor-info-value">{cotacao.compradorNome ?? '—'}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Fornecedores convidados</span>
                <span className="fornecedor-info-value">{cotacao.fornecedoresConvidados}</span>
              </div>
            </div>
            {/*
              "Quem passou a mão nela": os autores do histórico. Lista vazia é ausência de
              REGISTRO, nunca ausência de participação — a frase diz isso.
            */}
            <p className="fornecedor-modal-hint" style={{ marginTop: 16, marginBottom: 8 }}>
              Participaram do andamento desta cotação:
            </p>
            {cotacao.autores.length > 0 ? (
              <div className="fornecedor-contatos-lista">
                {cotacao.autores.map((autor) => (
                  <div key={autor} className="fornecedor-contato-linha">
                    <div className="fornecedor-contato-dados">
                      <span className="fornecedor-contato-nome">{autor}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="fornecedor-modal-vazio">
                Nenhuma participação registrada — esta cotação é anterior ao registro de autores.
              </p>
            )}
          </div>

          <div className="fornecedor-modal-secao">
            <h3>Aprovação do gestor</h3>
            <p className="fornecedor-modal-hint">
              A contagem cobre toda a história da cotação, e não só a última palavra: um item pode ter sido
              reprovado, revisto pelo comprador e aprovado depois.
            </p>
            <div className="fornecedor-info-grid">
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Resultado</span>
                <span className="fornecedor-info-value">{resumoGestor}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Itens aprovados</span>
                <span className="fornecedor-info-value">{cotacao.gestor ? cotacao.gestor.aprovadas : '—'}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Itens reprovados</span>
                <span className="fornecedor-info-value">{cotacao.gestor ? cotacao.gestor.reprovadas : '—'}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Última decisão</span>
                <span className="fornecedor-info-value">{formatarDataHora(cotacao.gestor?.ultimaEm ?? null)}</span>
              </div>
            </div>
            {!cotacao.gestor && (
              <p className="fornecedor-modal-hint" style={{ marginTop: 16, marginBottom: 0 }}>
                O gestor não foi acionado: todos os itens saíram no menor preço, então não houve o que aprovar.
              </p>
            )}
          </div>

          <div className="fornecedor-modal-secao">
            <h3>Ir para</h3>
            <p className="fornecedor-modal-hint">
              O passo a passo completo — convites, aberturas do link pelo fornecedor, propostas, escolhas,
              decisões do gestor e pedidos gerados — fica na Auditoria.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="pc-btn pc-btn--primario" onClick={onVerHistorico}>
                Ver histórico na Auditoria
              </button>
              <button type="button" className="pc-btn pc-btn--secundario" onClick={onAbrirCotacao}>
                Abrir a cotação
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CotacaoFinalizadaModal;
